import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Typography, Paper, IconButton } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Button, TextField, MdSpace, DatePicker, SmSpace } from 'm2m.components';
import { useDispatch, useSelector } from 'react-redux';
import { InstrumentoFinanceiro, InstrumentoFinanceiroApiResponse, HistoricoAtualizacaoCota } from 'common/genericTypes';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import moment, { Moment } from 'moment';
import { addAlert } from 'store/modules/snackBar';
import 'moment/locale/pt-br';
import Edit from '@material-ui/icons/Edit';
import Delete from '@material-ui/icons/Delete';
import Add from '@material-ui/icons/Add';
import { RootState } from 'store';
import { DataGridPro, GridCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { localizedTextsMap, componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import ModalFormulario, { FormularioDados } from './modalFormulario';

interface CustomToolbarProps {
  onNovoRegistro: () => void;
  disabled?: boolean;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({ onNovoRegistro, disabled = false }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
      <CustomToolBarUtils />
      <Button
        variant="contained"
        style={{ backgroundColor: disabled ? '#cccccc' : '#00E08E', color: '#fff' }}
        startIcon={<Add />}
        onClick={onNovoRegistro}
        size="small"
        disabled={disabled}
      >
        Novo Registro
      </Button>
    </div>
  );
};

const AtualizacaoCota: React.FC = () => {
  const dispatch = useDispatch();
  const getInstrumentosApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentos);
  const getCotasDeFundoByInstrumentoIdApi = useSelector((state: RootState) => state.cotasDeFundoApi.getCotasDeFundoByInstrumentoId);
  const getAtualizacoesCotasDeFundoApi = useSelector((state: RootState) => state.cotasDeFundoApi.getAtualizacoesCotasDeFundo);
  const postAtualizacaoCotasDeFundoApi = useSelector((state: RootState) => state.cotasDeFundoApi.postAtualizacaoCotasDeFundo);
  const putAtualizacaoCotasDeFundoApi = useSelector((state: RootState) => state.cotasDeFundoApi.putAtualizacaoCotasDeFundo);
  const deleteAtualizacaoCotasDeFundoApi = useSelector((state: RootState) => state.cotasDeFundoApi.deleteAtualizacaoCotasDeFundo);
  const postValidarDiasUteisApi = useSelector((state: RootState) => state.feriadosNacionais.postValidarDiasUteis);

  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [tipoFundo, setTipoFundo] = useState<string | undefined>(undefined);
  const [cotasDeFundoId, setCotasDeFundoId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [historico, setHistorico] = useState<HistoricoAtualizacaoCota[]>([]);
  const [dataInicio, setDataInicio] = useState<Moment | null>(null);
  const [dataFim, setDataFim] = useState<Moment | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [dadosEdicao, setDadosEdicao] = useState<HistoricoAtualizacaoCota | null>(null);
  const [minDate, setMinDate] = useState<Moment | null>(null);
  const [dataInicialFundo, setDataInicialFundo] = useState<Moment | null>(null);
  const [datasInvalidas, setDatasInvalidas] = useState<Moment[]>([]);
  const [diasUteisRestantes, setDiasUteisRestantes] = useState<number>(0);

  useEffect(() => {
    const fetchInstrumentos = async () => {
      try {
        const result = await getInstrumentosApi(
          dispatch,
          {
            tipoInstrumento: [InstrumentoFinanceiro.CotasDeFundo],
          },
          true
        );
        setInstrumentos(result);
      } catch (error) {
        console.error('Erro ao buscar instrumentos:', error);
      }
    };
    fetchInstrumentos();
  }, [dispatch, getInstrumentosApi]);

  const buscarInstrumentoDetalhes = async (instrumentoGuid: string) => {
    try {
      const cotasFundo = await getCotasDeFundoByInstrumentoIdApi(dispatch, instrumentoGuid, true);
      setCotasDeFundoId(cotasFundo.cotasDeFundoId);
      setTipoFundo(cotasFundo.tipoFundo);
      const dataMinima = cotasFundo.dataAutorizacaoCVM ?? cotasFundo.criteriosRemuneracao?.dataEmissao;
      const dataInicialMoment = moment(dataMinima);
      setMinDate(dataInicialMoment);
      setDataInicialFundo(dataInicialMoment);
      await buscarHistorico(cotasFundo.cotasDeFundoId, dataInicialMoment);
    } catch (error) {
      console.error('Erro ao buscar detalhes do instrumento:', error);
    }
  };

  useEffect(() => {
    const validarDiasUteis = async () => {
      if (!minDate) return;

      const listaDatas: Date[] = [];
      const dataAtual = moment();
      let dataIteracao = minDate.clone();

      while (dataIteracao.isSameOrBefore(dataAtual, 'day')) {
        listaDatas.push(dataIteracao.toDate());
        dataIteracao.add(1, 'day');
      }

      if (listaDatas.length === 0) {
        setDatasInvalidas([]);
        setDiasUteisRestantes(0);
        return;
      }

      try {
        const resultados = await postValidarDiasUteisApi(dispatch, listaDatas, true);

        const diasNaoUteis = resultados
          .filter(resultado => !resultado.diaUtil && resultado.dataValidada)
          .map(resultado => moment(resultado.dataValidada));

        const diasUteis = resultados.filter(resultado => resultado.diaUtil).length;

        setDatasInvalidas(diasNaoUteis);
        setDiasUteisRestantes(diasUteis);
      } catch (error) {
        console.error('Erro ao validar dias úteis:', error);
      }
    };

    validarDiasUteis();
  }, [minDate, dispatch, postValidarDiasUteisApi]);

  const buscarHistorico = async (cotasId: string, dataInicialParam?: Moment | null) => {
    try {
      const historicoDados = await getAtualizacoesCotasDeFundoApi(
        dispatch,
        cotasId,
        true
      );
      setHistorico(historicoDados || []);

      if (historicoDados && historicoDados.length > 0) {
        const dataMaisRecente = historicoDados.reduce((maisRecente, item) => {
          return moment(item.dataReferencia).isAfter(moment(maisRecente.dataReferencia)) ? item : maisRecente;
        });

        setMinDate(moment(dataMaisRecente.dataReferencia).add(1, 'day'));
      } else if (dataInicialParam) {
        setMinDate(dataInicialParam);
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      setHistorico([]);
    }
  };

  const fetchInstrumentos = async (value: any) => {
    if (!value) {
      setInstrumento(null);
      setCotasDeFundoId('');
      setHistorico([]);
      return;
    }
    if (!value?.instrumentoFinanceiroGuid) return;
    setInstrumento(value);
    await buscarInstrumentoDetalhes(value.instrumentoFinanceiroGuid);
  };

  const handleNovoRegistro = () => {
    setDadosEdicao(null);
    setModalOpen(true);
  };

  const handleEditarRegistro = (item: HistoricoAtualizacaoCota) => {
    setDadosEdicao(item);
    setModalOpen(true);
  };

  const handleExcluirRegistro = async (item: HistoricoAtualizacaoCota) => {
    if (!cotasDeFundoId) return;
    
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;

    setLoading(true);
    try {
      await deleteAtualizacaoCotasDeFundoApi(dispatch, cotasDeFundoId, item.id, true);
      dispatch(addAlert({ severity: 'success', message: 'Registro excluído com sucesso!' }));
      await buscarHistorico(cotasDeFundoId);
    } catch (error) {
      console.error('Erro ao excluir registro:', error);
      dispatch(addAlert({ severity: 'error', message: 'Erro ao excluir o registro. Tente novamente.' }));
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarModal = async (dados: FormularioDados) => {
    if (!cotasDeFundoId) return;

    setLoading(true);
    try {
      if (dadosEdicao) {
        await putAtualizacaoCotasDeFundoApi(
          dispatch,
          cotasDeFundoId,
          dadosEdicao.id,
          dados.dataReferencia,
          dados.valorCota,
          dados.patrimonioLiquido,
          dados.rentabilidadeDia,
          dados.rentabilidadeMes,
          dados.rentabilidadeAno,
          true
        );
        dispatch(addAlert({ severity: 'success', message: 'Registro atualizado com sucesso!' }));
      } else {
        await postAtualizacaoCotasDeFundoApi(
          dispatch,
          cotasDeFundoId,
          dados.dataReferencia,
          dados.valorCota,
          dados.patrimonioLiquido,
          dados.rentabilidadeDia,
          dados.rentabilidadeMes,
          dados.rentabilidadeAno,
          true
        );
        dispatch(addAlert({ severity: 'success', message: 'Registro criado com sucesso!' }));
      }
      setModalOpen(false);
      setDadosEdicao(null);
      await buscarHistorico(cotasDeFundoId);
    } catch (error) {
      console.error('Erro ao salvar registro:', error);
      dispatch(addAlert({ severity: 'error', message: 'Erro ao salvar o registro. Tente novamente.' }));
    } finally {
      setLoading(false);
    }
  };

  const filtrarHistorico = (): HistoricoAtualizacaoCota[] => {
    let historicoFiltrado = [...historico];

    if (dataInicio) {
      historicoFiltrado = historicoFiltrado.filter(item => 
        moment(item.dataReferencia).isSameOrAfter(dataInicio, 'day')
      );
    }

    if (dataFim) {
      historicoFiltrado = historicoFiltrado.filter(item => 
        moment(item.dataReferencia).isSameOrBefore(dataFim, 'day')
      );
    }

    return historicoFiltrado;
  };

  const handleLimparFiltros = () => {
    setDataInicio(null);
    setDataFim(null);
  };

  const formatarMoeda = (valor: number): string => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    }).format(valor);
  };

  const formatarPercentual = (valor: number): string => {
    const sinal = valor >= 0 ? '+' : '';
    return `${sinal}${valor.toFixed(4)}%`;
  };

  const getCorPercentual = (valor: number): string => {
    if (valor > 0) return '#4caf50';
    if (valor < 0) return '#f44336';
    return '#757575';
  };

  const getGridColumns = useCallback(
    (codigoInstrumento: string) => [
      {
        field: 'codigoAtivo',
        headerName: 'Código do Ativo',
        type: 'string',
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: () => codigoInstrumento,
      },
      {
        field: 'dataReferencia',
        headerName: 'Data de Referência',
        type: 'string',
        width: 180,
        minWidth: 150,
        maxWidth: 220,
        valueGetter: (params: GridValueGetterParams) => moment((params.row as HistoricoAtualizacaoCota).dataReferencia).format('DD/MM/YYYY'),
      },
      {
        field: 'valorCota',
        headerName: 'Valor da Cota',
        type: 'string',
        width: 180,
        minWidth: 150,
        maxWidth: 250,
        valueGetter: (params: GridValueGetterParams) => formatarMoeda((params.row as HistoricoAtualizacaoCota).valorCota),
      },
      {
        field: 'patrimonioLiquido',
        headerName: 'Patrimônio Líquido',
        type: 'string',
        width: 200,
        minWidth: 150,
        maxWidth: 300,
        valueGetter: (params: GridValueGetterParams) => formatarMoeda((params.row as HistoricoAtualizacaoCota).patrimonioLiquido),
      },
      {
        field: 'rentabilidadeDia',
        headerName: 'Rent. Dia',
        type: 'string',
        width: 130,
        minWidth: 100,
        maxWidth: 150,
        renderCell: (params: GridCellParams) => {
          const item = params.row as HistoricoAtualizacaoCota;
          return (
            <Typography style={{ color: getCorPercentual(item.rentabilidadeDia) }}>
              {formatarPercentual(item.rentabilidadeDia)}
            </Typography>
          );
        },
      },
      {
        field: 'rentabilidadeMes',
        headerName: 'Rent. Mês',
        type: 'string',
        width: 130,
        minWidth: 100,
        maxWidth: 150,
        renderCell: (params: GridCellParams) => {
          const item = params.row as HistoricoAtualizacaoCota;
          return (
            <Typography style={{ color: getCorPercentual(item.rentabilidadeMes) }}>
              {formatarPercentual(item.rentabilidadeMes)}
            </Typography>
          );
        },
      },
      {
        field: 'rentabilidadeAno',
        headerName: 'Rent. Ano',
        type: 'string',
        width: 130,
        minWidth: 100,
        maxWidth: 150,
        renderCell: (params: GridCellParams) => {
          const item = params.row as HistoricoAtualizacaoCota;
          return (
            <Typography style={{ color: getCorPercentual(item.rentabilidadeAno) }}>
              {formatarPercentual(item.rentabilidadeAno)}
            </Typography>
          );
        },
      },
      {
        field: 'acoes',
        headerName: 'Ações',
        type: 'string',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params: GridCellParams) => {
          const item = params.row as HistoricoAtualizacaoCota;
          return (
            <>
              <IconButton size="small" color="primary" onClick={() => handleEditarRegistro(item)}>
                <Edit />
              </IconButton>
              <IconButton size="small" style={{ color: '#f44336' }} onClick={() => handleExcluirRegistro(item)}>
                <Delete />
              </IconButton>
            </>
          );
        },
      },
    ],
    [handleEditarRegistro, handleExcluirRegistro]
  );

  return (
    <div style={{ marginRight: 40, marginBottom: 40 }}>
      <Typography variant="h2">Atualização de Cota e Rentabilidade do Fundo</Typography>
      <MdSpace />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" style={{ marginBottom: 16 }}>Dados do Fundo</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <InstrumentoAutocomplete
                  instrumentos={instrumentos}
                  onChange={async (value: any) => {
                    await fetchInstrumentos(value);
                  }}
                  instrumento={instrumento}
                  label="Selecione o Fundo *"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Código do Ativo"
                  value={instrumento?.codigo || ''}
                  fullWidth
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Emissor"
                  value={instrumento?.razaoSocialEmissor || ''}
                  fullWidth
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tipo de Fundo"
                  value={tipoFundo || ''}
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {instrumento && historico.length >= 0 && (
          <Grid item xs={12}>
            <Paper style={{ padding: 24 }}>
              <Typography variant="h6" style={{ marginBottom: 8 }}>
                Histórico do Fundo {instrumento?.codigo}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 24, color: '#757575' }}>
                Acompanhe o desempenho do fundo {instrumento?.codigo} ao longo do tempo
              </Typography>

              {minDate && moment(minDate).isSameOrAfter(moment(), 'day') ? (
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  <strong>Informação:</strong> Não há mais datas disponíveis para atualização. A última data lançada está atualizada com a data atual ou é futura.
                </Alert>
              ) : diasUteisRestantes > 0 ? (
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                  <strong>Atenção!</strong> Existem <strong>{diasUteisRestantes}</strong> dia(s) entre a última data cadastrada e hoje que ainda não possuem valores lançados.
                </Alert>
              ) : null}

              <Paper style={{ padding: 16, backgroundColor: '#fcfcfc' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="Data Inicial"
                      value={dataInicio}
                      onChange={(date: Moment | null) => setDataInicio(date)}
                      disableFuture
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="Data Final"
                      value={dataFim}
                      onChange={(date: Moment | null) => setDataFim(date)}
                      disableFuture
                      minDate={dataInicio == null ? undefined : dataInicio}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Button 
                      variant="outlined" 
                      style={{ backgroundColor: '#757575', color: '#fff', borderColor: '#757575' }}
                      onClick={handleLimparFiltros}
                    >
                      Limpar Filtros
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              <SmSpace />
              <DataGridPro
                autoHeight
                localeText={localizedTextsMap}
                componentsProps={componentsPropsValues}
                rows={filtrarHistorico()
                  .sort((a, b) => moment(b.dataReferencia).valueOf() - moment(a.dataReferencia).valueOf())
                  .map((item) => ({ ...item }))}
                columns={getGridColumns(instrumento?.codigo || '')}
                components={{
                  Toolbar: () => <CustomToolbar 
                    onNovoRegistro={handleNovoRegistro} 
                    disabled={minDate ? moment(minDate).isSameOrAfter(moment(), 'day') : false}
                  />,
                }}
                disableSelectionOnClick
                rowsPerPageOptions={rowsPerPageOptionsValues}
                pagination
                initialState={{
                  pagination: {
                    pageSize: 10,
                  },
                }}
              />
            </Paper>
          </Grid>
        )}
      </Grid>

      <ModalFormulario
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setDadosEdicao(null);
        }}
        onSave={handleSalvarModal}
        dadosEdicao={dadosEdicao}
        loading={loading}
        minDate={minDate}
        datasInvalidas={datasInvalidas}
      />
    </div>
  );
};

export default AtualizacaoCota;
